import { CopyButton } from "@tracify/ui/copy-button";
// Component for displaying tracking parameters
import { Input } from "@tracify/ui/input";
import { Button } from "@tracify/ui/button";
import { Card, CardContent, CardHeader } from "@tracify/ui/card";
import Image from "next/image";
import { BookOpenIcon } from "@heroicons/react/24/outline";
import { PlatformConfig } from "./tracking-config";

export default function TrackingParameterSection({
  config,
}: {
  config: PlatformConfig;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
            <Image
              src={config.img}
              alt={`${config.name} logo`}
              width={24}
              height={24}
              className="w-8 h-8 object-contain"
            />
          </div>
          <span className="text-xl font-bold text-foreground font-space-grotesk">
            {config.name} - Tracking Parameters
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          <h3 className="text-sm font-bold text-foreground font-space-grotesk">
            Tracking parameters
          </h3>

          {config.extra ? (
            <>
              <p className="text-sm dark:text-white text-black font-space-grotesk">
                To track campaigns you send on {config.name} the tracking is
                implemented via the standard UTM parameters:
              </p>
              <div className="pt-1">
                <ul className="list-disc list-inside">
                  {config.extra.map((extra) => (
                    <li
                      className="text-sm dark:text-white text-black font-space-grotesk"
                      key={extra.details}
                    >
                      <strong>{extra.parameter}:</strong> Please set this
                      parameter to {extra.details}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm dark:text-[rgb(250,250,250,0.56)] text-black font-space-grotesk">
                Copy the following tracking parameters and add them to the
                tracking settings of your active ads. These parameters should be
                placed in the URL Parameters field found in additional tracking
                settings.
              </p>

              <Input
                value={config.parameters}
                readOnly
                variant="default"
                className="font-space-grotesk"
                endAdornment={
                  <CopyButton
                    value={config.parameters}
                    copyLabel="tracking parameters"
                  />
                }
              />
            </>
          )}
        </div>

        {config.setupGuideUrl && (
          <div className="mt-2">
            <a
              href={config.setupGuideUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="ghost"
                className="text-xs text-foreground font-space-grotesk"
                size="sm"
              >
                <BookOpenIcon className="h-4 w-4 mr-2" />
                Learn how to set up {config.name} tracking
              </Button>
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
